import React, { useState, useEffect } from 'react';
import { teamAPI } from '../services/api';
import { Users, UserPlus, RefreshCw, Trash2, Mail, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TeamPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'operator' });
  const [inviting, setInviting] = useState(false);

  useEffect(() => { fetchTeam(); }, []);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      const res = await teamAPI.list();
      setMembers(res.data.members || []);
    } catch (err) {
      console.error('Team error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) return;
    setInviting(true);
    try {
      await teamAPI.invite(inviteForm);
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'operator' });
      fetchTeam();
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка приглашения');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Удалить пользователя из команды?')) return;
    try {
      await teamAPI.remove(id);
      fetchTeam();
    } catch (err) {
      alert('Ошибка удаления');
    }
  };

  const handleRoleChange = async (id, role) => {
    try {
      await teamAPI.updateRole(id, role);
      fetchTeam();
    } catch (err) {
      alert('Ошибка изменения роли');
    }
  };

  const roleLabels = { owner: 'Владелец', admin: 'Админ', accountant: 'Бухгалтер', operator: 'Оператор' };
  const roleBadges = {
    owner: 'bg-purple-500/20 text-purple-400',
    admin: 'bg-blue-500/20 text-blue-400',
    accountant: 'bg-emerald-500/20 text-emerald-400',
    operator: 'bg-slate-500/20 text-slate-400'
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-white">Команда</h1>
        {user?.role === 'owner' && (
          <button onClick={() => setShowInviteModal(true)} className="btn-primary flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Пригласить
          </button>
        )}
      </div>

      {/* Role Info */}
      <div className="card p-4">
        <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Права ролей</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div><span className="text-purple-400">Владелец</span><p className="text-slate-400">Полный доступ</p></div>
          <div><span className="text-blue-400">Админ</span><p className="text-slate-400">Платежи, настройки webhook</p></div>
          <div><span className="text-emerald-400">Бухгалтер</span><p className="text-slate-400">Платежи, выплаты, отчёты</p></div>
          <div><span className="text-slate-400">Оператор</span><p className="text-slate-400">Только просмотр платежей</p></div>
        </div>
      </div>

      {/* Team List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-4 text-sm font-medium text-slate-400">Пользователь</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Роль</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Статус</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Добавлен</th>
                {user?.role === 'owner' && <th className="text-right p-4 text-sm font-medium text-slate-400">Действия</th>}
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium">{m.name?.[0] || m.email[0].toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-white">{m.name || 'Без имени'}</p>
                        <p className="text-slate-400 text-sm">{m.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {user?.role === 'owner' && m.role !== 'owner' ? (
                      <select value={m.role} onChange={e => handleRoleChange(m.id, e.target.value)} className="input py-1 px-2 text-sm">
                        <option value="admin">Админ</option>
                        <option value="accountant">Бухгалтер</option>
                        <option value="operator">Оператор</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleBadges[m.role]}`}>{roleLabels[m.role]}</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${m.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {m.status === 'active' ? 'Активен' : 'Приглашён'}
                    </span>
                  </td>
                  <td className="p-4 text-slate-400 text-sm">{new Date(m.created_at).toLocaleDateString('ru-RU')}</td>
                  {user?.role === 'owner' && (
                    <td className="p-4 text-right">
                      {m.role !== 'owner' && (
                        <button onClick={() => handleRemove(m.id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Mail className="w-5 h-5" /> Пригласить сотрудника</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email</label>
                <input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} className="input w-full" placeholder="user@example.com" />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Роль</label>
                <select value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))} className="input w-full">
                  <option value="admin">Админ</option>
                  <option value="accountant">Бухгалтер</option>
                  <option value="operator">Оператор</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={() => setShowInviteModal(false)} className="btn-secondary flex-1">Отмена</button>
              <button onClick={handleInvite} disabled={inviting} className="btn-primary flex-1">
                {inviting ? 'Отправка...' : 'Пригласить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;
